import React, { useState, useRef } from 'react';
import { Box, Button, Card, CardContent, Typography, TextField, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function SubmitWork({ clubId, projectId, projectName, clubName, team, user, submission, fetchData }) {
  const [submitDialog, setSubmitDialog] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null); // <-- store selected file

  const theme = useTheme();
  const submitValuesRef = useRef({
    submitURL: '',
    message: ''
  });

  const handleClickOpen = () => {
    setSubmitDialog(true);
    submitValuesRef.current.submitURL = '';
    setFile(null);
  };

  const handleClose = () => {
    setSubmitDialog(false);
  };

  const handleChange = (e) => {
    submitValuesRef.current[e.target.name] = e.target.value;
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!submitValuesRef.current.submitURL && !file) {
      newErrors.submitURL = "Provide a link or upload a file";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      if (!(clubId && projectId && team?.name && user)) throw new Error("club Id/project id/team name/user missing");

      let fileURL = null;
      if (file) {
        const storage = getStorage();
        const storageRef = ref(storage, `Submissions/${clubId}_${projectId}_${team.name}_${user}/${file.name}`);
        await uploadBytes(storageRef, file);
        fileURL = await getDownloadURL(storageRef);
      }

      const customId = `${clubId}_${projectId}_${team.name}_${user}`;
      const submissionData = {
        club_id: clubId,
        project_id: projectId,
        project_name: projectName,
        club_name: clubName,
        team: team.name,
        user: user,
        submission_link: submitValuesRef.current.submitURL || fileURL,
        message: submitValuesRef.current.message,
        file_url: fileURL, // <-- save file path/url separately
        timestamp: serverTimestamp(),
      };

      await setDoc(doc(db, "submissions", customId), { submissionData });

      setLoading(false);
      submitValuesRef.current = { submitURL: '', message: '' };
      setFile(null);
      setSubmitDialog(false);
      fetchData();
    } catch (error) {
      setLoading(false);
      console.error("Error in submission:", error);
    }
  };

  const CreateTeamDialog = (
    <Dialog
      disableEscapeKeyDown
      maxWidth='sm'
      open={submitDialog}
      onClose={handleClose}
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Submit your work</DialogTitle>
      <DialogContent sx={{ minWidth: '600px' }}>
        <TextField
          fullWidth
          label="Submission Link"
          name="submitURL"
          id="submitURL"
          margin="normal"
          defaultValue={submitValuesRef.current.submitURL}
          onChange={handleChange}
          error={!!errors.submitURL}
          helperText={errors.submitURL || 'Provide the link to your submission (or upload a file)'}
        />
        <TextField
          fullWidth
          label="Message"
          name="message"
          id="message"
          margin="normal"
          defaultValue={submitValuesRef.current.message}
          onChange={handleChange}
        />
        <Box mt={2}>
          <Typography variant="body2">Upload File (optional)</Typography>
          <input type="file" onChange={handleFileChange} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant='text' onClick={handleClose}>Cancel</Button>
        <Button variant='contained' type="submit" disabled={loading}>Submit</Button>
      </DialogActions>
    </Dialog>
  );

  const SubmittedData = (
    <>
      <Box display='flex'>
        <Typography fontWeight="bold" textAlign="start" gutterBottom>
          Link:
        </Typography>
        <Typography variant="body2" textAlign="start" ml={1}>
          <Link target="_blank" underline="none" to={submission?.submission_link}>{submission?.submission_link}</Link>
        </Typography>
      </Box>
      {submission?.file_url && (
        <Box display='flex'>
          <Typography fontWeight="bold">File:</Typography>
          <Typography variant="body2" ml={1}>
            <a href={submission.file_url} target="_blank" rel="noopener noreferrer">Download File</a>
          </Typography>
        </Box>
      )}
      <Box display='flex' flexGrow={1}>
        <Typography fontWeight="bold" textAlign="start">Message:</Typography>
        <Typography variant="body2" textAlign="start" ml={1}>{submission?.message}</Typography>
      </Box>
      <Button sx={{ mt: 2 }} variant="contained" onClick={handleClickOpen}>
        Re-submit your work
      </Button>
    </>
  );

  return (
    <Box>
      {CreateTeamDialog}
      <Card sx={{ my: 4 }} variant="outlined">
        <CardContent>
          <Typography variant='h6' fontWeight="bold" textAlign="start" mb={2}>
            Your Submission
          </Typography>
          {loading && 'Loading...'}
          {!loading && !submission && <Typography mb={2}>You do not have any submissions!</Typography>}
          {!loading && submission ? SubmittedData : !loading && (
            <Button variant="contained" onClick={handleClickOpen}>
              Submit your work
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
